import type { ReactNode } from 'react'

/**
 * Shared diagram primitives.
 *
 * Every diagram is inline SVG or flow layout — no images, no WebGL. Each carries a
 * text equivalent so the meaning does not depend on the picture or on colour alone.
 */

export function Figure({
  label,
  children,
  wide,
}: {
  /** Text equivalent of the diagram, for assistive technology. */
  label: string
  children: ReactNode
  wide?: boolean
}) {
  return (
    <figure className={`figure${wide ? ' figure--wide' : ''}`} role="group">
      <div className="figure__body" role="img" aria-label={label}>
        {children}
      </div>
    </figure>
  )
}

/** A horizontal chain of stages: `A → B → C`. The workhorse of this deck. */
export function Chain({
  steps,
  tone = 'river',
  emphasis,
}: {
  steps: { label: string; detail?: string }[]
  tone?: 'river' | 'land' | 'earth'
  /** Index of the step to highlight. */
  emphasis?: number
}) {
  return (
    <ol className={`chain chain--${tone}`}>
      {steps.map((step, i) => (
        <li key={step.label} className={`chain__step${emphasis === i ? ' is-emphasis' : ''}`}>
          <div className="chain__box">
            <span className="chain__label">{step.label}</span>
            {step.detail ? <span className="chain__detail">{step.detail}</span> : null}
          </div>
          {i < steps.length - 1 ? (
            <span className="chain__arrow" aria-hidden="true">
              <svg viewBox="0 0 24 12" width="24" height="12" fill="none">
                <path d="M0 6h20M15 1.5 20.5 6 15 10.5" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

/** A vertical stack of layers, revealed top to bottom. */
export function LayerStack({
  layers,
}: {
  layers: { title: string; detail?: string; items?: string[]; tone?: 'river' | 'land' | 'earth' | 'ink' }[]
}) {
  return (
    <ol className="stack">
      {layers.map((layer, i) => (
        <li key={layer.title} className={`stack__layer stack__layer--${layer.tone ?? 'river'}`}>
          <div className="stack__head">
            <span className="stack__title">{layer.title}</span>
            {layer.detail ? <span className="stack__detail">{layer.detail}</span> : null}
          </div>
          {layer.items?.length ? (
            <ul className="stack__items">
              {layer.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
          {i < layers.length - 1 ? (
            <span className="stack__arrow" aria-hidden="true">
              <svg viewBox="0 0 12 22" width="12" height="22" fill="none">
                <path d="M6 0v18M1.5 13.5 6 18l4.5-4.5" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

/** A responsive grid of cards. */
export function CardGrid({
  cards,
  columns = 4,
  tone = 'river',
}: {
  cards: { title: string; detail?: string; meta?: string }[]
  columns?: 2 | 3 | 4
  tone?: 'river' | 'land' | 'earth' | 'mixed'
}) {
  return (
    <ul className={`cardgrid cardgrid--${columns} cardgrid--${tone}`}>
      {cards.map((card, i) => (
        <li key={card.title} className="cardgrid__card" style={{ ['--i' as string]: i }}>
          {card.meta ? <span className="cardgrid__meta">{card.meta}</span> : null}
          <span className="cardgrid__title">{card.title}</span>
          {card.detail ? <span className="cardgrid__detail">{card.detail}</span> : null}
        </li>
      ))}
    </ul>
  )
}

/** Frame for a conceptual product screen. Always labelled — never a fake screenshot. */
export function MockFrame({
  title,
  children,
  chrome = true,
}: {
  title: string
  children: ReactNode
  chrome?: boolean
}) {
  return (
    <div className="mock">
      {chrome ? (
        <div className="mock__chrome" aria-hidden="true">
          <span className="mock__dot" />
          <span className="mock__dot" />
          <span className="mock__dot" />
          <span className="mock__addr">{title}</span>
        </div>
      ) : null}
      <div className="mock__body">{children}</div>
      <p className="mock__label">Conceptual LAMS Operations Experience</p>
    </div>
  )
}

/** Node in a radial relationship diagram. */
export function Orbit({
  centre,
  nodes,
}: {
  centre: { title: string; detail?: string }
  nodes: string[]
}) {
  return (
    <div className="orbit">
      <div className="orbit__centre">
        <span className="orbit__centre-title">{centre.title}</span>
        {centre.detail ? <span className="orbit__centre-detail">{centre.detail}</span> : null}
      </div>
      <ul className="orbit__ring">
        {nodes.map((node, i) => (
          <li key={node} className="orbit__node" style={{ ['--i' as string]: i, ['--n' as string]: nodes.length }}>
            {node}
          </li>
        ))}
      </ul>
    </div>
  )
}
